/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.querySelectorAll('.cart-item__remove').forEach(e => e.addEventListener('click', (evt) => { evt.preventDefault(); this.handleClick(evt.target.getAttribute('data-key'),evt.target.getAttribute('data-id'),evt.target.getAttribute('data-product-id')) }));
    }

    handleCartItemChange(key, variantId, productId, quantity) {
      this.updateQuantity(key, variantId, productId, quantity);
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(key, variantId, productId) {
      // if (!evt.target.matches('.js-remove-item')) return;
      // evt.preventDefault();
      this.updateQuantity(key, variantId, productId, 0);
    }

    /**
     * Updates the quantity of a line item.
     * @param {string} key - Line item key.
     * @param {string} variantId - Line item variantId.
     * @param {string} productId - Line item productId.
     * @param {number} quantity - Quantity to set.
     */
    async updateQuantity(key, variantId, productId, quantity) {

      this.enableLoading();

      if (quantity <= 0) {
        const removedRow = this.querySelector(`.cart-item[data-line-item-key="${key}"]`);
        if (removedRow) {
          removedRow.nextElementSibling?.remove();
          removedRow.remove();
        }
      }

      try {

        const before_updated_cart = LocalCart.getState();
        const before_updated_sub_total = before_updated_cart.total_price;

        LocalCart.updateQuantity(variantId, quantity);

        const updated_cart = LocalCart.getState();
        const updated_sub_total = updated_cart.total_price;
        let isShippingUpdateNeeded = false;

        // Local cart update event so latest cart saved to metafield
        let isUpdateSuccess;
        if(sharedCart && Object.keys(sharedCart).length){
          isUpdateSuccess = await sharedCart.handleCartChange();
        }

        if(!isUpdateSuccess){
          if (window.checkout) {
            window.checkout.loading = false;
            window.checkout._showCartErrorMessage(`Cart update failed. try again...`);
          }
          await this.refreshCartItems();
          return;
        }

        //refresh the assortment page if is open
        localStorage.setItem('tab_refresh_trigger', Date.now().toString());

        // remove shipping cost local storage if cart is empty.
        if(updated_cart.item_count <= 0) localStorage.removeItem('checkout_shipping');

        // Remove shipping cost from localStorage to fetch the latest rate when the cart is updated,
        // only if the free shipping threshold has been reached
        const cartElement = document.querySelector(`cart-checkout`);
        if(updated_cart.item_count > 0){
          //shipping logic;
          const shippingFreeThreshold = Number(cartElement?.shippingFreeThreshold || 30000)/100;
          if(((before_updated_sub_total/100) < shippingFreeThreshold && (updated_sub_total/100) >= shippingFreeThreshold || (before_updated_sub_total/100) > shippingFreeThreshold && (updated_sub_total/100) <= shippingFreeThreshold)){
            localStorage.removeItem('checkout_shipping');
            isShippingUpdateNeeded = true;
          }
          // bonus logic; it recalculates `bonus_amount` before the summary is
          // repainted, but it must never prevent that repaint - a throw here
          // used to leave the markup stale while the local cart was already
          // updated, so failures are logged and swallowed.
          try {
            await cartElement?.updateSubmitedBonus();
          } catch (bonusError) {
            console.error('❌ Bonus recalculation failed:', bonusError);
          }
        }

        // Fast client-side UI update instead of re-rendering the whole cart
        // section via the Section Rendering API. Falls back to the full render
        // when the cart is now empty (needs the empty-cart markup) or when the
        // fast path can't safely compute the totals.
        let fastUpdated = false;
        if (updated_cart.item_count > 0) fastUpdated = this.updateCartUI();

        if (!fastUpdated) {
          await this.refreshCartItems();
          return;
        }

        // Refresh the estimated shipping (async; updates its own DOM node).
        if(isShippingUpdateNeeded){
          try {
            await cartElement?._handleShippingCosts?.();
          } catch (shippingError) {
            console.error('❌ Shipping cost refresh failed:', shippingError);
          }
        }
      } catch (error) {
        // The local cart (and the metafield) are already updated by this point,
        // so never leave stale markup on screen - fall back to a full render.
        console.error('❌ Error updating cart item:', error);
        try {
          await this.refreshCartItems();
        } catch (renderError) {
          console.error('❌ Cart re-render failed:', renderError);
        }
      } finally {
        // Always release the loading overlay/spinner enableLoading() switched on.
        if (window.checkout) window.checkout.loading = false;
      }
    }

    /**
     * Fast, client-side cart UI update.
     *
     * @returns {boolean} `true` if the fast update was applied; `false` if the
     *   caller should fall back to `refreshCartItems()` (e.g. deposit data, the
     *   money formatter or the summary markup unavailable).
     */
    updateCartUI() {
      // Bail out to the full section render when the pieces we rely on are missing.
      if (typeof window.formatMoney !== 'function') return false;
      const taxRates = window.theme?.config?.taxRates;
      if (!taxRates) return false;
      // The only summary row that is always rendered - if it is absent we are
      // not looking at the cart summary markup this method knows how to patch.
      if (!document.querySelector('[data-cart-final-total]')) return false;

      const cart = LocalCart.getState();
      const items = Array.isArray(cart.items) ? cart.items : [];

      const rateE = Number(taxRates.TAX_RATE_E?.rate) || 0;
      const rateDefault = Number(taxRates.TAX_RATE_DEFAULT?.rate) || 0;

      // Deposit (Pfand) prices are only available when the deposit-pricing-json
      // snippet is present on the page. If any line references a deposit and we
      // can't price it, fall back to the full render so the total stays correct.
      const depositPriceMap = {};
      const depositPricesEl = document.querySelector('[data-deposit-prices]');
      if (depositPricesEl) {
        try {
          JSON.parse(depositPricesEl.innerText).forEach((d) => {
            depositPriceMap[d.id] = Number(d.price);
          });
        } catch (e) { /* leave map empty */ }
      }
      const hasDepositData = !!depositPricesEl;

      // The Liquid summary only accumulates savings/discounts when
      // `settings.cart_show_savings` is on, so mirror it here or the fast total
      // would disagree with the one rendered on the next page load.
      const showSavings = document.getElementById('cart-summary')?.dataset.showSavings !== 'false';

      let totalSaving = 0;
      let subtotal = 0; // total_excl_deposit
      let totalTax = 0;
      let totalDeposit = 0;
      let totalDiscount = 0;

      for (const item of items) {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        const compareAt = Number(item.compare_at_price) || 0;
        const props = item.properties || {};
        const isDeposit = Object.prototype.hasOwnProperty.call(props, '_deposit');

        if (showSavings && compareAt > price) {
          totalSaving += (compareAt - price) * qty;
        }

        if (!isDeposit) {
          const lineTotal = Number.isFinite(Number(item.final_line_price))
            ? Number(item.final_line_price)
            : price * qty;
          subtotal += lineTotal;

          const taxBase = (Number(props._price) || price) * qty;
          const rate = item.metafields?.variant?.europe1_price_factory_ptg === 'E' ? rateE : rateDefault;
          totalTax += Math.round(taxBase * rate - taxBase);
        }

        const depositRef = props._deposit_reference;
        if (depositRef != null && `${depositRef}`.trim() !== '') {
          if (!hasDepositData) return false; // can't price the deposit → full render
          const depPrice = depositPriceMap[parseInt(depositRef, 10)];
          if (depPrice != null) totalDeposit += depPrice * qty;
        }

        if (showSavings) {
          (item.line_level_discount_allocations || []).forEach((a) => {
            totalDiscount += Number(a.amount) || 0;
          });
        }
      }

      if (showSavings) {
        (cart.cart_level_discount_applications || []).forEach((d) => {
          totalDiscount += Number(d.total_allocated_amount) || 0;
        });
      }

      const bonusRaw = cart.attributes?.bonus_amount;
      const bonusValue = bonusRaw != null && `${bonusRaw}`.trim() !== ''
        ? Math.round(Number(bonusRaw) * 100)
        : 0;

      const depositTaxSingle = totalDeposit * rateDefault - totalDeposit;
      const subTotalWithDeposit = subtotal + totalDeposit - bonusValue;
      const finalTotalTax = totalTax + depositTaxSingle;
      const totalInclTax = subTotalWithDeposit + finalTotalTax;
      const finalTotal = totalInclTax - totalDiscount;

      // ---- Patch summary DOM (only when the rendered value changed) ----
      const withCurrency = (cents) => window.formatMoney(cents);
      const noCurrency = (cents) => window.formatMoney(cents, window.theme?.settings?.moneyFormat);

      this._setSummaryValue('[data-tax-value]', withCurrency(finalTotalTax));
      this._setSummaryValue('[data-cart-final-total]', withCurrency(finalTotal));

      this._setSummaryValue('[data-subtotal]', withCurrency(subtotal), '.subtotal-excl-deposit', subtotal > 0);
      this._setSummaryValue('[data-cart-saving]', withCurrency(totalSaving), '.cart-total-saving', totalSaving > 0);
      this._setSummaryValue('[data-p-fand]', withCurrency(totalDeposit), '.total-deposit', totalDeposit > 0);
      this._setSummaryValue('[data-cart-discount]', `&minus;${withCurrency(totalDiscount)}`, '.cart-total-discount', totalDiscount > 0);
      this._setSummaryValue('[data-bonus-value]', `&minus; ${withCurrency(bonusValue)}`, '.bonus-value', bonusValue > 0);

      // ---- Patch line item prices & quantity ----
      // Re-sync every row rather than only the changed one: a quantity change
      // also shifts the discount allocations on the remaining lines.
      items.forEach((item) => {
        const row = this._findItemRow(item);
        if (!row) return;

        const qty = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        const lineTotal = Number.isFinite(Number(item.final_line_price))
          ? Number(item.final_line_price)
          : price * qty;
        const compareAt = Number(item.compare_at_price) || 0;

        const rawCurrent = row.querySelector('.item-raw-price .price__current');
        if (rawCurrent) this._setNodeHTML(rawCurrent, noCurrency(price));

        const finalCurrent = row.querySelector('.item-final-price .price__current');
        if (finalCurrent) this._setNodeHTML(finalCurrent, noCurrency(lineTotal));

        // Supercents compare-at total (only rendered when the variant is reduced).
        if (compareAt > price) {
          const finalCompare = row.querySelector('.item-final-price .price__compare');
          if (finalCompare) this._setNodeHTML(finalCompare, noCurrency(compareAt * qty));
        }

        const qtyHost = row.querySelector('quantity-input');
        const qtyInput = (qtyHost || row).querySelector('.qty-input__input');
        // Skip the field the shopper is currently editing - its own debounced
        // change handler reconciles it.
        if (qtyInput && document.activeElement !== qtyInput) {
          if (`${qtyInput.value}` !== `${qty}`) qtyInput.value = qty;
          qtyInput.setAttribute('data-initial-value', qty);
          // Keep <quantity-input>'s cached quantity in step with the field,
          // otherwise its `quantity !== currentQty` guard swallows the next change.
          if (qtyHost) qtyHost.currentQty = qty;
        }
      });

      return true;
    }

    /**
     * Finds the rendered row for a LocalCart line item.
     *
     * Matching falls back to the variant id because the cart section is rendered
     * from Shopify's cart (`item.key` is Shopify's `variantId:hash`) while
     * LocalCart stores its own client-generated keys - the two only line up when
     * the local cart was hydrated from the Shopify cart. The variant id is the
     * identifier both sides always agree on, and is what `LocalCart.updateQuantity`
     * itself matches on, so one line per variant is an invariant of the model.
     *
     * @param {object} item - LocalCart line item.
     * @returns {HTMLElement|null} The matching row, or `null`.
     */
    _findItemRow(item) {
      if (item.key) {
        const byKey = this.querySelector(`.cart-item[data-line-item-key="${item.key}"]`);
        if (byKey) return byKey;
      }

      const variantId = item.variant_id != null ? item.variant_id : item.id;
      if (variantId == null || `${variantId}`.trim() === '') return null;

      return this.querySelector(`.cart-item[data-variant-id="${variantId}"]`);
    }

    /**
     * Sets the HTML of the element matched by `selector` only when it differs,
     * and optionally toggles the visibility of a wrapper (to mirror Liquid's
     * `{% if value > 0 %}` conditional rows).
     * @param {string} selector - Target element selector.
     * @param {string} html - New inner HTML.
     * @param {string} [wrapperSelector] - Optional wrapper whose visibility follows `visible`.
     * @param {boolean} [visible] - Whether the wrapper (and value) should show.
     */
    _setSummaryValue(selector, html, wrapperSelector, visible) {
      if (wrapperSelector) {
        const wrapper = document.querySelector(wrapperSelector);
        if (wrapper) wrapper.classList.toggle('hidden-i', visible === false);
        if (visible === false) return;
      }
      const el = document.querySelector(selector);
      if (el) this._setNodeHTML(el, html);
    }

    /**
     * Updates an element's innerHTML only when the value actually changed.
     * @param {HTMLElement} el - Element to update.
     * @param {string} html - New inner HTML.
     */
    _setNodeHTML(el, html) {
      if (el.innerHTML.trim() !== `${html}`.trim()) el.innerHTML = html;
    }

    /**
     * Refresh cart items section
     * Called from other components like product-form and cart-drawer
     */
    async refreshCartItems() {
      console.log('🔄 Refreshing cart items...');
      
      // If we're on the cart page, use the checkout render method
      if (window.location.pathname.includes(`/cart`) && window.checkout && window.checkout.render) {
        await window.checkout.render();
      } else {
        // Otherwise use the global render function
        window.location.reload();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading() {
      if (window.checkout) window.checkout.loading = true;
      document.activeElement?.blur();
    }
  }

  customElements.define('cart-items', CartItems);
}
